import type { ITenantAdminRepository, TenantAdminQueryParams, PaginatedResponse } from '@yawi/domain';
import { buildQueryString } from '@yawi/domain';
import type {
    CreateTenantDto,
    OnboardTenantDto,
    OnboardTenantResponse,
    TenantAdmin,
    TenantCreateResponse,
    TenantDashboard,
    TenantProvisionResponse,
    UpdateTenantDto
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiTenantAdminRepository implements ITenantAdminRepository {
  private readonly basePath = 'v1/admin/tenants';

  async findAll(params?: TenantAdminQueryParams): Promise<PaginatedResponse<TenantAdmin>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      status: params?.status,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder?.toLowerCase() as 'asc' | 'desc' | undefined,
    });
    return client.get<PaginatedResponse<TenantAdmin>>(
      queryString ? `${this.basePath}?${queryString}` : this.basePath,
    );
  }

  async findById(id: string): Promise<TenantAdmin> {
    const client = getApiClient();
    const response = await client.get<TenantAdmin>(`${this.basePath}/${id}`);
    return response;
  }

  async findBySlug(slug: string): Promise<TenantAdmin> {
    const client = getApiClient();
    const response = await client.get<TenantAdmin>(`${this.basePath}/slug/${slug}`);
    return response;
  }

  async create(dto: CreateTenantDto): Promise<TenantCreateResponse> {
    const client = getApiClient();
    const response = await client.post<TenantCreateResponse>(this.basePath, dto);
    return response;
  }

  async update(id: string, dto: UpdateTenantDto): Promise<TenantAdmin> {
    const client = getApiClient();
    const response = await client.put<TenantAdmin>(`${this.basePath}/${id}`, dto);
    return response;
  }

  async delete(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.basePath}/${id}`);
  }

  async onboard(dto: OnboardTenantDto): Promise<OnboardTenantResponse> {
    const client = getApiClient();
    const response = await client.post<OnboardTenantResponse>(`${this.basePath}/onboard`, dto);
    return response;
  }

  async suspend(id: string): Promise<TenantAdmin> {
    const client = getApiClient();
    const response = await client.post<TenantAdmin>(`${this.basePath}/${id}/suspend`);
    return response;
  }

  async activate(id: string): Promise<TenantAdmin> {
    const client = getApiClient();
    const response = await client.post<TenantAdmin>(`${this.basePath}/${id}/activate`);
    return response;
  }

  async provision(id: string): Promise<TenantProvisionResponse> {
    const client = getApiClient();
    const response = await client.post<TenantProvisionResponse>(`${this.basePath}/${id}/provision`);
    return response;
  }

  async getDashboard(id: string): Promise<TenantDashboard> {
    const client = getApiClient();
    // HttpClient ya desenvuelve { success, data } automáticamente
    return client.get<TenantDashboard>(`${this.basePath}/${id}/dashboard`);
  }
}
